<template>
  <div v-loading="loading" class="app-container">
    <div v-show="showSelection">
      <div>
        <label style="margin-right:10px">{{ $t('common.domain') }}</label>
        <el-select
          v-model="selectedDomainID"
          :disabled="loading"
          class="search-select"
          type="select"
          style="width: 20%; margin-bottom: 10px"
          :placeholder="$t('common.domain')"
        >
          <el-option v-for="domain in domains" :key="domain.id" :label="domain.name" :value="domain.id" />
        </el-select>
      </div>
      <div>
        <label style="margin-right:10px">{{ $t('priceListItemPrice.priceType') }}</label>
        <el-select
          v-model="selectedPriceTypeOption"
          class="search-select"
          type="select"
          style="width: 20%; margin-bottom: 10px"
          :placeholder="$t('priceListItemPrice.priceType')"
          @change="handlePriceTypeChange"
        >
          <el-option
            v-for="priceTypeOption in priceTypeOptions"
            :key="priceTypeOption.code"
            :label="priceTypeOption.description"
            :value="priceTypeOption.code"
          />
        </el-select>
      </div>
      <div v-show="isWorkshopPriceType()" style="margin-bottom:10px">
        <label style="margin-right:10px">{{ $t('common.country') }}</label>
        <el-select
          v-model="selectedCountryID"
          class="search-select"
          type="select"
          style="width: 20%; margin-bottom: 10px"
          :placeholder="$t('common.country')"
        >
          <el-option v-for="country in countries" :key="country.id" :label="country.name" :value="country.id" />
        </el-select>
      </div>
      <editor-button
        :disabled="!canShowPrices() || loading"
        button-translation="priceListItemPrice.showPrices"
        action="primary"
        :icon-only="false"
        @click="showPrices()"
      />
    </div>
    <div v-show="!showSelection">
      <el-row class="page-buttons">
        <label style="font-size:1.4em">{{ title }}</label>
      </el-row>
      <div style="clear:both;">
        <div style="float:left;margin-bottom:5px">
          <editor-button
            button-icon="el-icon-back"
            icon-only
            action="primary"
            :loading="loading"
            style="float:left"
            :tooltip="$t('common.back')"
            @click="back()"
          />
          <el-input-number v-model="percentageChange" style="float:left;margin-left:5px;" placeholder="%" />
          <el-select
            v-model="selectedCurrencyRounding"
            class="search-select"
            type="select"
            style="float:left;margin-left:5px"
            :placeholder="$t('priceListItemPrice.rounding')"
          >
            <el-option
              v-for="option in currencyRoundingOptions"
              :key="option.value"
              :label="option.description"
              :value="option.value"
            />
          </el-select>
          <editor-button
            :disabled="!canCalculatePrices()"
            button-translation="priceListItemPrice.calculatePrices"
            action="primary"
            :loading="loading"
            :icon-only="false"
            style="float:left;margin-left:5px;"
            @click="calculatePrices()"
          />
        </div>
        <div style="float:right;margin-bottom:5px">
          <el-date-picker
            v-model="selectedEffectiveDate"
            type="date"
            format="dd-MM-yyyy"
            value-format="yyyy-MM-dd"
            :clearable="false"
            :placeholder="$t('priceListItemPrice.effectiveDate')"
            :picker-options="pickerOptions"
          />
          <editor-button
            :disabled="!canSave()"
            :icon-only="true"
            action="save"
            :loading="loading"
            button-translation="common.save"
            @click="save()"
          />
        </div>
      </div>
      <el-table
        :data="items"
        border
        fit
        highlight-current-row
        style="width: 100%;"
        height="700"
        @selection-change="handleSelectionChange"
      >
        <el-table-column type="selection" width="40" align="center" :selectable="() => true"></el-table-column>
        <el-table-column :label="$t('priceListItem.productCode')" property="priceListItemProductCode" width="180">
          <template slot-scope="scope">
            <span>{{ scope.row['priceListItemProductCode'] }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('common.description')" property="priceListItemDescription">
          <template slot-scope="scope">
            <span>{{ scope.row['priceListItemDescription'] }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('priceListItem.currentPrice')" property="price" width="120" align="right">
          <template slot-scope="scope">
            <span>{{ scope.row['price'] }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('priceListItem.newPrice')" width="120" align="right">
          <template slot-scope="{ row }">
            <MDinput
              v-model="row.newPrice"
              type="text"
              custom-classes="align-text-right default-font-size default-text-color"
            />
          </template>
        </el-table-column>
        <el-table-column :label="$t('common.currency')" property="currency" width="120" align="center">
          <template slot-scope="scope">
            <span>{{ scope.row['currency'] }}</span>
          </template>
        </el-table-column>
        <el-table-column
          :label="$t('priceListItemPrice.effectiveDate')"
          property="currentEffectiveDate"
          width="120"
          align="center"
        >
          <template slot-scope="scope">
            <span>{{ scope.row['currentEffectiveDate'] }}</span>
          </template>
        </el-table-column>
        <el-table-column
          :label="$t('priceListItemPrice.expirationDate')"
          property="currentExpirationDate"
          width="120"
          align="center"
        >
          <template slot-scope="scope">
            <span>{{ scope.row['currentExpirationDate'] }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('priceListItem.nextPrice')" property="nextPrice" width="120" align="right">
          <template slot-scope="scope">
            <span>{{ scope.row['nextPrice'] }}</span>
          </template>
        </el-table-column>
      </el-table>
    </div>
  </div>
</template>

<script>
import EditorButton from '@/components/crud/EditorButton';
import { PriceTypeOption } from '@/utils/constants';
import MDinput from '@/components/MDinput';

export default {
  components: {
    EditorButton,
    MDinput
  },
  data() {
    return {
      items: [],
      loading: false,
      percentageChange: undefined,
      selectedCountryID: undefined,
      selectedCurrencyRounding: undefined,
      selectedDomainID: this.$store.getters['domain/getDefaultSelectValueDomainID'],
      selectedEffectiveDate: undefined,
      selectedItems: [],
      selectedPriceTypeOption: null,
      showSelection: true
    };
  },
  computed: {
    currencyRoundingOptions() {
      return [
        { value: 1, description: '1 cent' },
        { value: 5, description: '5 cent' },
        { value: 10, description: '10 cent' },
        { value: 100, description: '100 cent' }
      ];
    },
    countries() {
      return this.$store.getters['country/loadedItems'];
    },
    domains() {
      return this.$store.getters.domains;
    },
    pickerOptions() {
      return {
        disabledDate(givenDate) {
          const currentDate = new Date();
          currentDate.setHours(0, 0, 0, 0);
          return new Date(givenDate) < currentDate - 1;
        },
        firstDayOfWeek: 1
      };
    },
    priceTypeOptions() {
      return this.$store.getters['option/loadedOptions']('priceType').filter(
        item => item.code === PriceTypeOption.SellingPrice || item.code === PriceTypeOption.WorkshopPrice
      );
    },
    title() {
      const domainName = this.getDomainName();
      const priceTypeDescription = this.getPriceTypeDescription();
      const countryName = this.getCountryName();
      const title = `${priceTypeDescription} ${domainName}`;
      return countryName ? `${title} ${countryName}` : title;
    }
  },
  created() {
    this.$store.dispatch('country/getItems');
  },
  methods: {
    back() {
      this.showSelection = true;
    },
    canCalculatePrices() {
      return this.percentageChange !== undefined && !!this.selectedCurrencyRounding && this.selectedItems.length !== 0;
    },
    calculatePrices() {
      if (!this.canCalculatePrices()) {
        return;
      }
      if (this.selectedItems.length === 0) {
        return;
      }
      const selectedItems = this.selectedItems
        .filter(i => !!i.priceListItemPriceID)
        .map(i => ({
          priceListItemPriceID: i.priceListItemPriceID,
          price: i.price
        }));
      this.loading = true;
      this.$store
        .dispatch('priceListItem/calculatePrices', {
          percentageChange: this.percentageChange,
          rounding: this.selectedCurrencyRounding,
          items: selectedItems
        })
        .then(data => data.items.forEach(this.setNewPrice))
        .finally(() => {
          this.loading = false;
        });
    },
    canSave() {
      return !!this.selectedEffectiveDate;
    },
    canShowPrices() {
      const show = this.selectedDomainID !== null && this.selectedPriceTypeOption !== null;
      return this.isWorkshopPriceType() ? show && !!this.selectedCountryID : show;
    },
    getCountryName() {
      const id = this.selectedCountryID;
      const country = this.countries.find(item => item.id === id);
      return country && country.name;
    },
    getDomainName() {
      const id = this.selectedDomainID;
      const domain = this.domains.find(item => item.id === +id);
      return domain && domain.name;
    },
    getPriceType() {
      return this.priceTypeOptions.find(item => item.code === this.selectedPriceTypeOption);
    },
    getPriceTypeDescription() {
      const option = this.getPriceType();
      return option && option.description;
    },
    handlePriceTypeChange() {
      const priceType = this.getPriceType();
      if (!priceType || priceType.code !== PriceTypeOption.WorkshopPrice) {
        this.selectedCountryID = undefined;
      }
    },
    handleSelectionChange(items) {
      this.selectedItems = items;
    },
    isWorkshopPriceType() {
      return this.selectedPriceTypeOption === PriceTypeOption.WorkshopPrice;
    },
    save() {
      this.loading = true;
      this.$store
        .dispatch('priceListItem/changePrices', {
          domainID: this.selectedDomainID,
          priceType: this.selectedPriceTypeOption,
          countryID: this.selectedCountryID,
          effectiveDate: this.selectedEffectiveDate,
          items: this.items
            .filter(i => !!i.newPrice)
            .map(i => ({
              priceListItemID: i.priceListItemID,
              newPrice: i.newPrice
            }))
        })
        .then(data => {
          this.showPrices();
        })
        .finally(() => {
          this.loading = false;
        });
    },
    setNewPrice(newPriceItem) {
      const item = this.items.find(i => i.priceListItemPriceID === newPriceItem.priceListItemPriceID);
      if (item) {
        item.newPrice = newPriceItem.newPrice;
      }
    },
    showPrices() {
      this.loading = true;
      this.$store
        .dispatch('priceListItem/getCurrentPrices', {
          domainID: this.selectedDomainID,
          priceType: this.selectedPriceTypeOption,
          countryID: this.selectedCountryID
        })
        .then(data => {
          this.items = data.items.map(item => ({
            ...item,
            newPrice: null
          }));
          this.showSelection = false;
        })
        .finally(() => {
          this.loading = false;
        });
    }
  }
};
</script>
